// Change #100665 - "way too many buttons now, can we have a modal for the sim
// which opens then we can see stats and add proper buttons".
// Change #100667 - "need ability to refresh from emnify so we can see if its
// online, attached, 4g, etc".
//
// One SIM: who has it, what it is, its live state from emnify (refreshed when
// the modal opens and on demand) and this month's usage, with every action as
// a labelled button. The actions are the list page's own handlers, passed in.
// `actions` is optional: the company "SIM Connectivity" screen (Change #100666)
// opens this with `companyView` (no provider names), its own `loadLive`, and
// only the "Allocate to" staff picker.
import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Col, Input, Row, Spinner } from "reactstrap";
import SimCardOutlinedIcon from "@mui/icons-material/SimCardOutlined";
import AppModal from "../../../../Components/Common/AppModal";
import { getMasterSimLive } from "../../../../helpers/fakebackend_helper";
import {
  connectivityBadge,
  displayBic,
  formatMb,
  simType,
  statusColor,
} from "./simHelpers";

const Field = ({ label, children }) => (
  <div className="mb-2">
    <div className="small text-muted">{label}</div>
    <div className="fw-medium text-break">
      {children || children === 0 ? (
        children
      ) : (
        <span className="text-muted">—</span>
      )}
    </div>
  </div>
);

const SimDetailModal = ({
  sim,
  onClose,
  onRefreshed,
  actions,
  loadLive = getMasterSimLive,
  // Change #100666 - the company's own view: the SIM is theirs, so "Assigned
  // to" is the person, and the Super-Admin-only hints are left out. Paul:
  // "dont show we use emnify" - in this view nothing names the provider, and
  // the API sends FlagLiveCapable / FlagOnDevice instead of the emnify ids.
  companyView = false,
  // Company view: "they need to be able to allocate to a staff/user member".
  // staffOptions = [{Id, FullName}], onAllocate(staffId|null) -> Promise.
  staffOptions = null,
  onAllocate = null,
}) => {
  const [live, setLive] = useState(null);
  const [usage, setUsage] = useState(null);
  const [liveError, setLiveError] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [allocateTo, setAllocateTo] = useState("");
  const [allocating, setAllocating] = useState(false);

  const simId = sim?.Id;
  const linked = companyView
    ? Number(sim?.FlagLiveCapable) === 1
    : !!sim?.EmnifySimId;
  const onDevice = companyView
    ? Number(sim?.FlagOnDevice) === 1
    : !!sim?.EmnifyEndpointId;
  const liveTitle = companyView ? "Live status" : "Live from emnify";

  useEffect(() => {
    setAllocateTo(sim?.CurrentStaff_Id ? String(sim.CurrentStaff_Id) : "");
  }, [sim?.Id, sim?.CurrentStaff_Id]);

  const saveAllocation = async () => {
    if (!onAllocate) return;
    setAllocating(true);
    await onAllocate(allocateTo ? Number(allocateTo) : null);
    setAllocating(false);
  };

  const refresh = useCallback(async () => {
    if (!simId) return;
    setLoading(true);
    setLiveError("");
    const res = await loadLive(simId);
    setLoading(false);
    if (res?.status === 200) {
      setLive(res.live || null);
      setUsage(res.usage || null);
      setRefreshedAt(new Date());
      if (onRefreshed) onRefreshed(res);
    } else {
      setLiveError(
        res?.message ||
          (companyView
            ? "Could not get the live status"
            : "Could not refresh from emnify")
      );
    }
    // onRefreshed is a list refresh; deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simId, loadLive]);

  useEffect(() => {
    setLive(null);
    setUsage(null);
    setLiveError("");
    setRefreshedAt(null);
    if (simId && linked) refresh();
  }, [simId, linked, refresh]);

  if (!sim) return null;

  const type = simType(sim);
  const conn = connectivityBadge(live?.connectivity);
  const isActivated = Number(sim.StatusId) === 1;
  const isAssigned = !!sim.CurrentCompany_Id;
  // Run an action from the list page, closing this modal first when the
  // action opens a popup of its own.
  const run =
    (fn, close = true) =>
    () => {
      if (close) onClose();
      fn(sim);
    };

  return (
    <AppModal
      open={!!sim}
      onClose={onClose}
      title={sim.Label || "SIM"}
      subtitle={sim.Iccid ? `ICCID ${sim.Iccid}` : "No ICCID"}
      icon={<SimCardOutlinedIcon />}
      headerAccent="indigo"
      size="md"
      hideFooter
    >
      {/* Identity */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <Badge color={statusColor(sim.StatusId)}>
          {sim.StatusDescription || "Unknown status"}
        </Badge>
        {type.kind === "esim" && (
          <Badge color="info">
            <i className="ri-qr-code-line align-middle me-1"></i>eSIM
          </Badge>
        )}
        {type.kind === "physical" && (
          <Badge color="light" className="text-dark border">
            <i className="ri-sim-card-line align-middle me-1"></i>Physical SIM
          </Badge>
        )}
        {linked && live && (
          <Badge color={conn.color} title={conn.hint}>
            {conn.text}
          </Badge>
        )}
        {live?.network_type && (
          <Badge color="primary">{live.network_type}</Badge>
        )}
      </div>

      <Row>
        <Col sm={6}>
          <Field label={companyView ? "Used by" : "Assigned to"}>
            {companyView
              ? sim.CurrentStaffName || "Not given to anyone yet"
              : isAssigned
              ? `${
                  sim.CurrentCompanyName || `Company #${sim.CurrentCompany_Id}`
                }${sim.CurrentStaffName ? ` - ${sim.CurrentStaffName}` : ""}`
              : "In stock"}
          </Field>
          {companyView ? (
            <Field label="Device">
              {onDevice ? live?.device_name || "Connected to a device" : null}
            </Field>
          ) : (
            <Field label="Device (emnify endpoint)">
              {sim.EmnifyEndpointId
                ? `#${sim.EmnifyEndpointId}${
                    live?.device_name ? ` ${live.device_name}` : ""
                  }`
                : null}
            </Field>
          )}
          <Field label="Type">{type.detail || null}</Field>
        </Col>
        <Col sm={6}>
          <Field label="MSISDN">{sim.Msisdn}</Field>
          <Field label="IMSI">{sim.Imsi}</Field>
          {sim.Eid && <Field label="EID">{sim.Eid}</Field>}
          {sim.Bic && <Field label="BIC">{displayBic(sim.Bic)}</Field>}
        </Col>
      </Row>

      {/* Company view - give the SIM to one of their own staff. */}
      {companyView && onAllocate && (
        <div className="border rounded p-2 mb-3">
          <strong className="small text-uppercase d-block mb-2">
            Allocate to
          </strong>
          <div className="d-flex gap-2">
            <Input
              type="select"
              bsSize="sm"
              value={allocateTo}
              onChange={(e) => setAllocateTo(e.target.value)}
              disabled={allocating || staffOptions === null}
            >
              <option value="">
                {staffOptions === null
                  ? "Loading staff..."
                  : "Nobody (not allocated)"}
              </option>
              {(staffOptions || []).map((s) => (
                <option key={s.Id} value={s.Id}>
                  {s.FullName ||
                    [s.FirstName, s.LastName].filter(Boolean).join(" ") ||
                    `Staff #${s.Id}`}
                </option>
              ))}
            </Input>
            <Button
              color="primary"
              size="sm"
              onClick={saveAllocation}
              disabled={
                allocating ||
                allocateTo ===
                  (sim.CurrentStaff_Id ? String(sim.CurrentStaff_Id) : "")
              }
            >
              {allocating ? <Spinner size="sm" /> : "Save"}
            </Button>
          </div>
        </div>
      )}

      {/* Live status */}
      <div className="border rounded p-2 mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong className="small text-uppercase">{liveTitle}</strong>
          {linked && (
            <Button
              color="light"
              size="sm"
              className="border"
              onClick={refresh}
              disabled={loading}
            >
              {loading ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <i className="ri-refresh-line align-middle me-1"></i>
              )}
              Refresh
            </Button>
          )}
        </div>
        {!linked ? (
          <div className="small text-muted">
            {companyView
              ? "Live status is not available for this SIM."
              : "Added manually, so emnify has nothing live for it."}
          </div>
        ) : liveError ? (
          <div className="small text-danger">{liveError}</div>
        ) : !live && loading ? (
          <div className="small text-muted">
            <Spinner size="sm" className="me-2" />
            {companyView ? "Checking the network..." : "Asking emnify..."}
          </div>
        ) : live ? (
          <>
            {!onDevice && (
              <div className="small text-danger mb-2">
                Not on a device, so it passes no data.
                {companyView
                  ? " Contact support to make it live."
                  : " Use Make live."}
              </div>
            )}
            {live.error && (
              <div className="small text-warning mb-2">{live.error}</div>
            )}
            <Row>
              <Col sm={6}>
                <Field label="Connection">
                  {live.connectivity ? `${conn.text} - ${conn.hint}` : null}
                </Field>
                <Field label="Network">
                  {[live.network_type, live.operator, live.country]
                    .filter(Boolean)
                    .join(" · ") || null}
                </Field>
                <Field label="Last seen">{live.last_seen}</Field>
              </Col>
              <Col sm={6}>
                <Field label="Data session">
                  {live.session_started
                    ? `Since ${live.session_started}${
                        live.session_duration
                          ? ` (${live.session_duration})`
                          : ""
                      }`
                    : "None open"}
                </Field>
                <Field label="IP address">{live.ip_address}</Field>
                <Field label="IMEI">{live.imei}</Field>
              </Col>
            </Row>
            {usage && (
              <Row className="text-center g-2 mt-1">
                <Col xs={6} md={3}>
                  <div className="border rounded p-2">
                    <div className="small text-muted">Data this month</div>
                    <div className="fw-semibold">
                      {formatMb(usage.current_month_data_mb)}
                    </div>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="border rounded p-2">
                    <div className="small text-muted">SMS this month</div>
                    <div className="fw-semibold">{usage.current_month_sms}</div>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="border rounded p-2">
                    <div className="small text-muted">Data last month</div>
                    <div className="fw-semibold">
                      {formatMb(usage.last_month_data_mb)}
                    </div>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="border rounded p-2">
                    <div className="small text-muted">SMS last month</div>
                    <div className="fw-semibold">{usage.last_month_sms}</div>
                  </div>
                </Col>
              </Row>
            )}
            {refreshedAt && (
              <div className="small text-muted mt-2">
                Refreshed {refreshedAt.toLocaleTimeString()}
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Actions - labelled, grouped */}
      {actions && (
        <>
          <div className="d-flex flex-wrap gap-2">
            {sim.EmnifySimId && (
              <Button color="success" size="sm" onClick={run(actions.makeLive)}>
                <i className="ri-signal-tower-line align-middle me-1"></i>Make
                live
              </Button>
            )}
            {isActivated ? (
              <Button
                color="warning"
                size="sm"
                onClick={run(actions.suspend, false)}
              >
                <i className="ri-pause-circle-line align-middle me-1"></i>
                Suspend
              </Button>
            ) : (
              <Button
                color="success"
                outline
                size="sm"
                onClick={run(actions.activate, false)}
              >
                <i className="ri-play-circle-line align-middle me-1"></i>
                Activate
              </Button>
            )}
            {sim.Iccid && type.kind !== "physical" && (
              <Button
                color="dark"
                outline
                size="sm"
                onClick={run(actions.esimQr)}
              >
                <i className="ri-qr-code-line align-middle me-1"></i>eSIM QR
                code
              </Button>
            )}
          </div>
          <div className="d-flex flex-wrap gap-2 mt-2">
            <Button
              color="info"
              outline
              size="sm"
              onClick={run(actions.assign)}
            >
              <i className="ri-user-shared-line align-middle me-1"></i>
              {isAssigned ? "Change company / person" : "Assign to a company"}
            </Button>
            {isAssigned && (
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={run(actions.unassign)}
              >
                <i className="ri-logout-box-line align-middle me-1"></i>Return
                to stock
              </Button>
            )}
            <Button
              color="primary"
              outline
              size="sm"
              onClick={run(actions.history)}
            >
              <i className="ri-history-line align-middle me-1"></i>History
            </Button>
            <Button
              color="primary"
              outline
              size="sm"
              onClick={run(actions.edit)}
            >
              <i className="ri-pencil-line align-middle me-1"></i>Edit
            </Button>
            <Button
              color="danger"
              outline
              size="sm"
              className="ms-auto"
              onClick={run(actions.remove)}
            >
              <i className="ri-delete-bin-line align-middle me-1"></i>Delete
            </Button>
          </div>
        </>
      )}
    </AppModal>
  );
};

export default SimDetailModal;
